import type { ErrorEvent } from "@sentry/browser";

const ANONYMOUS_FRAME = "<anonymous>";
const NATIVE_FRAME = "[native code]";

const stripQueryAndHash = (url: string): string => url.split(/[?#]/)[0];

/**
 * True when an event has a stacktrace in which not one frame belongs to page code.
 *
 * Browser extensions and in-app-browser agents inject their scripts as `<anonymous>`, or leave
 * nothing but `[native code]` Promise frames, so their throws carry no filename to match with
 * `denyUrls`, under messages too generic to put in `ignoreErrors` without also hiding our own bugs
 * (FORMS-WEB-XS, QP, YM). An event with no first-party frame at all has nothing in it to act on.
 *
 * A frame at the document URL itself is foreign too once its line number is above 1: the HTML
 * forms-web serves is a single line (one external `main.<hash>.js` plus a tiny inline stub), while
 * WKWebView attributes host-injected WKUserScripts — the iOS in-app browser form scanner in
 * Chrome / the Google app — to the page URL with the injected script's own line numbers
 * (FORMS-WEB-YE, YJ).
 *
 * Events carrying no stacktrace are NOT foreign by this rule — they stay reportable, since a
 * stackless rejection can still come from our own async code.
 */
export const hasOnlyForeignFrames = (event: ErrorEvent): boolean => {
  const frames = event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? [];

  if (frames.length === 0) {
    return false;
  }

  const documentUrl = window.location.origin + window.location.pathname;

  return frames.every(
    (frame) =>
      !frame.filename ||
      frame.filename === ANONYMOUS_FRAME ||
      frame.filename === NATIVE_FRAME ||
      (stripQueryAndHash(frame.filename) === documentUrl && (frame.lineno ?? 0) > 1),
  );
};
